import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';

import { Menu } from '@components/molecules/Menu';
import { CollapsedMenu } from '@components/molecules/CollapsedMenu';
import { navigationRoutesSelector } from '@recoil/selectors/navigationRoutes';
import { useStyles } from './style';
import PaybyrdLogo from '@components/atoms/PaybyrdLogo';
import { useUser } from '@hooks/useUser';
import { toggleMenu } from '@store/reducers/ui';

export function SidebarMenu({ offset }) {
  const dispatch = useDispatch();
  const { isAIAssistantEnabled, hasIncompleteOnboardingStore } = useUser();
  const { menuOpen } = useSelector((state) => state.ui);
  const classes = useStyles({ offset, menuOpen });
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const [currentMenuIndex, setCurrentMenu] = useState(
    hasIncompleteOnboardingStore ? 'getting-started' : 'dashboard'
  );
  const { topItems, bottomItems } = useRecoilValue(navigationRoutesSelector);

  // Filter the topItems to conditionally toggle GettingStarted/Dashboard menu options
  const filteredTopItems = topItems.filter((item) => {
    if (hasIncompleteOnboardingStore && item.slug === 'dashboard') {
      return false;
    }

    if (!hasIncompleteOnboardingStore && item.slug === 'getting-started') {
      return false;
    }

    return true;
  });

  // Filter the bottomItems to conditionally include AI assistant menus based on isAIAssistantEnabled
  const filteredBottomItems = bottomItems.filter((item) => {
    // Filter out AI assistant menus if AI is not enabled for any store
    if (item.id === 'ai-smart-assistant-menu') {
      return isAIAssistantEnabled === true;
    }
    return true;
  });

  return (
    <Fragment>
      <Drawer
        sx={{
          display: { xs: 'none', md: 'block' },
          paddingTop: '0px',
        }}
        variant="permanent"
        className={clsx(
          classes.drawer,
          menuOpen ? classes.drawerOpen : classes.drawerCollapsed
        )}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            menuOpen ? classes.drawerOpen : classes.drawerPaperCollapsed
          ),
        }}
      >
        <Box className={clsx(classes.logoContainer)}>
          <PaybyrdLogo id="sidebar-logo" open={menuOpen} />
          <IconButton
            aria-label={menuOpen ? 'Collapse menu' : 'Expand menu'}
            onClick={() => dispatch(toggleMenu())}
            size="small"
            className={classes.toggleButton}
          >
            {menuOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>

        <Divider className={classes.headerSeparator} />

        {!menuOpen ? (
          <CollapsedMenu
            filteredTopItems={filteredTopItems}
            filteredBottomItems={filteredBottomItems}
            offset={offset}
            menuOpen={menuOpen}
            currentMenuIndex={currentMenuIndex}
            setCurrentMenu={setCurrentMenu}
          />
        ) : (
          <Menu
            offset={offset}
            topItems={filteredTopItems}
            bottomItems={filteredBottomItems}
            onClose={() => setMobileMenuIsOpen(false)}
            currentMenuIndex={currentMenuIndex}
            setCurrentMenu={setCurrentMenu}
          />
        )}
      </Drawer>

      <AppBar
        sx={{ display: { md: 'none', xs: 'block' } }}
        position="sticky"
        className={classes.appBar}
      >
        <Toolbar
          classes={{ root: classes.toolbar, gutters: classes.toolbarGutters }}
        >
          <PaybyrdLogo id="logo-mobile" sx={{ marginTop: '15px' }} />
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={() => setMobileMenuIsOpen(!mobileMenuIsOpen)}
            className={classes.menuButton}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor={'top'}
        variant="temporary"
        open={mobileMenuIsOpen}
        onClose={() => setMobileMenuIsOpen(!mobileMenuIsOpen)}
        classes={{
          paper: clsx(classes.drawerPaper, classes.drawerPaperMobile),
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px',
          }}
        >
          <PaybyrdLogo id="logo-mobile-expanded" />
          <IconButton onClick={() => setMobileMenuIsOpen(false)} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <Menu
          offset={offset}
          topItems={filteredTopItems}
          bottomItems={filteredBottomItems}
          onClose={() => setMobileMenuIsOpen(false)}
          currentMenuIndex={currentMenuIndex}
          setCurrentMenu={setCurrentMenu}
        />
      </Drawer>
    </Fragment>
  );
}

export default SidebarMenu;
